import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import Alert from '../common/Alert';

interface Permission {
  id: string;
  permission_key: string;
  category: string;
  action: string;
  description?: string;
  is_system: boolean;
}

interface Role {
  id: string;
  name: string;
  description: string;
  is_bootstrap: boolean;
  permissions: Permission[];
}

interface RoleSaveData {
  id?: string;
  name: string;
  description: string;
  permissionIds: string[];
}

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (role: RoleSaveData) => Promise<void>;
  role?: Role | null;
  availablePermissions: Permission[];
}

export default function RoleModal({
  isOpen,
  onClose,
  onSave,
  role,
  availablePermissions,
}: RoleModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissionIds: [] as string[],
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        description: role.description,
        permissionIds: role.permissions.map(p => p.id),
      });
    } else {
      setFormData({
        name: '',
        description: '',
        permissionIds: [],
      });
    }
    setError(null);
  }, [role, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Role name is required');
      return;
    }

    if (formData.permissionIds.length === 0) {
      setError('At least one permission must be selected');
      return;
    }

    setLoading(true);
    try {
      await onSave({
        id: role?.id,
        name: formData.name,
        description: formData.description,
        permissionIds: formData.permissionIds,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permissionId)
        ? prev.permissionIds.filter(id => id !== permissionId)
        : [...prev.permissionIds, permissionId],
    }));
  };

  const selectAllInCategory = (category: string) => {
    const categoryPermissions = availablePermissions
      .filter(p => p.category === category)
      .map(p => p.id);
    
    const allSelected = categoryPermissions.every(id => 
      formData.permissionIds.includes(id)
    );

    if (allSelected) {
      // Deselect all in category
      setFormData(prev => ({
        ...prev,
        permissionIds: prev.permissionIds.filter(
          id => !categoryPermissions.includes(id)
        ),
      }));
    } else {
      // Select all in category
      setFormData(prev => ({
        ...prev,
        permissionIds: [...new Set([...prev.permissionIds, ...categoryPermissions])],
      }));
    }
  };

  const filteredPermissions = availablePermissions.filter(p =>
    p.permission_key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedPermissions = filteredPermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {role ? 'Edit Role' : 'Create New Role'}
            </h2>
            {role?.is_bootstrap && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                System role — name is locked, but permissions can be updated.
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="p-6 overflow-y-auto flex-1 min-h-0">
            {error && (
              <Alert type="error" onClose={() => setError(null)} className="mb-4">
                {error}
              </Alert>
            )}

            {/* Basic Info */}
            <div className="space-y-4 mb-6">
              <Input
                label="Role Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Field Supervisor"
                required
                disabled={role?.is_bootstrap}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the role's responsibilities..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={role?.is_bootstrap}
                />
              </div>
            </div>

            {/* Permissions Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Permissions ({formData.permissionIds.length} selected)
                </h3>
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search permissions..."
                  className="max-w-xs"
                />
              </div>

              <div className="space-y-4">
                {Object.entries(groupedPermissions).map(([category, permissions]) => {
                  const categorySelected = permissions.filter(p => 
                    formData.permissionIds.includes(p.id)
                  ).length;
                  const allSelected = categorySelected === permissions.length;

                  return (
                    <div
                      key={category}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900 dark:text-white capitalize">
                          {category} ({categorySelected}/{permissions.length})
                        </h4>
                        <button
                          type="button"
                          onClick={() => selectAllInCategory(category)}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {allSelected ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {permissions.map((permission) => (
                          <label
                            key={permission.id}
                            className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.permissionIds.includes(permission.id)}
                              onChange={() => togglePermission(permission.id)}
                              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {permission.permission_key}
                              </div>
                              {permission.description && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {permission.description}
                                </div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : role ? 'Update Role' : 'Create Role'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Made with Bob